import moment from 'moment';
import momentTz from 'moment-timezone';
import {mapActions, mapGetters} from 'vuex';
import ContactSettingsController from '@/services/Contacts/SettingsController';
import Translation from '@/services/Translation';
import store from "../store";

export default {

    computed: {

        ...mapGetters([
            'authUser',
            'userAbilities',
            'projectAbilities',
            'userSettings',
            'isGlobalManagementSystem',
            'hasClOnlyFeatures',
            'systemLocale',
            'latestScrollToComment',
        ])

    },

    methods: {

        ...mapActions(['loadUserSettings']),

        /**
         * Company abilities
         *
         * @param {string} ability
         *
         * @returns {boolean}
         */
        userCan(ability) {
            if (this.userIsCommonLinkSuperAdmin()) {
                return true;
            }

            return this.userAbilities.indexOf(ability) > -1;
        },

        /**
         * @param {string} ability
         * @param {string} projectKey
         *
         * @returns {boolean}
         */
        userCanInProject(ability, projectKey) {
            if (this.userIsCommonLinkSuperAdmin()) {
                return true;
            }

            return projectKey in this.projectAbilities && this.projectAbilities[projectKey].indexOf(ability) > -1;
        },

        /**
         * @param {string} ability
         *
         * @returns {boolean}
         */
        userCanInAnyProject(ability) {
            if (this.userIsCommonLinkSuperAdmin()) {
                return true;
            }

            for (const project in this.projectAbilities) {
                if (this.projectAbilities[project].indexOf(ability) > -1) {
                    return true;
                }
            }

            return false;
        },

        /**
         * @returns {boolean}
         */
        userIsCommonLinkSuperAdmin() {
            return this.userAbilities.indexOf('*') > -1;
        },

        /**
         * @returns {boolean}
         */
        userIsCommonLinkUser() {
            return this.authUser.is_common_link_user;
        },

        /**
         * @param {string} key
         * @param {*} value
         *
         * @returns {*|AxiosPromise}
         */
        userSettingSet(key, value) {
            return ContactSettingsController.save(this.authUser.secret_key, key, value).then(() => {
                this.loadUserSettings();
            });
        },

        /**
         * @param {string} key
         * @param {*} defaultValue
         *
         * @return {*}
         */
        userSettingGet(key, defaultValue = null) {
            return this.userSettings.hasOwnProperty(key) ? this.userSettings[key] : defaultValue;
        },

        /**
         * Set the page title
         *
         * @param {string} title
         */
        setPageTitle(title) {
            if (title) {
                document.title = `${title} - jibit`;
            } else {
                document.title = `jibit`;
            }
        },

        /**
         * @param {string} dateString
         *
         * @returns {moment}
         */
        fromServerDateFormat(dateString) {
            return moment(dateString, 'YYYY-MM-DD HH:mm:ss');
        },

        /**
         * @param {string} dateString
         *
         * @returns {momentTz}
         */
        fromServerDatetimeFormat(dateString) {
            return momentTz.tz(dateString, 'YYYY-MM-DD HH:mm:ss', 'Europe/Berlin');
        },

        /**
         * @param {string} timeString
         *
         * @returns {momentTz}
         */
        fromServerTimeFormat(timeString) {
            return momentTz.tz(timeString, 'HH:mm:ss', 'Europe/Berlin');
        },

        /**
         * @param {string} dateString
         * @param {string|null} [format]
         * @param {string|null} [timezone]
         *
         * @returns {string}
         */
        toServerDateFormat(dateString, format = null, timezone = null) {
            timezone = timezone || this.authUser.timezone;
            format = format || 'DD/MMM/YY';

            return moment(new Date(dateString), format).format('YYYY-MM-DD');
        },

        /**
         * @param {string} dateString
         * @param {string|null} [format]
         * @param {string|null} [timezone]
         *
         * @returns {string}
         */
        toServerTimeFormat(dateString, format = null, timezone = null) {
            timezone = timezone || this.authUser.timezone;
            format = format || 'h:mm A';

            return momentTz.tz(dateString, format, timezone).tz('Europe/Berlin').format('HH:mm:ss');
        },

        /**
         * @param {string} dateString
         * @param {string|null} [format]
         * @param {string|null} [timezone]
         *
         * @returns {string}
         */
        toServerDatetimeFormat(dateString, format = null, timezone = null) {
            timezone = timezone || this.authUser.timezone;
            format = format || 'DD/MMM/YY h:mm A';

            return momentTz.tz(dateString, format, timezone).tz('Europe/Berlin').format('YYYY-MM-DD HH:mm:ss');
        },

        /**
         * @param {string} dateString
         * @param {string} format
         *
         * @returns {string}
         */
        userFormattedDate(dateString, format = 'DD/MMM/YY') {
            return this.fromServerDateFormat(dateString).format(format);
        },

        /**
         * @param {string} dateString
         * @param {string} format
         *
         * @returns {string}
         */
        userFormattedDatetime(dateString, format = 'DD/MMM/YY h:mm A') {
            const timezone = this.authUser.timezone || momentTz.tz.guess();

            return this.fromServerDatetimeFormat(dateString).tz(timezone).format(format);
        },

        /**
         * @param {string} timeString
         * @param {string} format
         *
         * @returns {string}
         */
        userFormattedTime(timeString, format = 'hh:mm A') {
            const timezone = this.authUser.timezone || momentTz.tz.guess();

            return this.fromServerTimeFormat(timeString).tz(timezone).format(format);
        },

        /**
         * @param {string} dateString
         *
         * @returns {string}
         */
        diffForHumans(dateString) {
            const timezone = this.authUser.timezone || momentTz.tz.guess();
            const date = this.fromServerDatetimeFormat(dateString).tz(timezone);
            const now = this.currentDate();

            return (date.diff(now) > 0) ? date.toNow() : date.fromNow();
        },

        /**
         * @returns {momentTz}
         */
        currentDate() {
            const timezone = this.authUser.timezone || momentTz.tz.guess();

            return momentTz().tz(timezone);
        },

        /**
         * @param {string} format
         */
        currentDateFormatted(format = 'DD/MMM/YY') {
            return this.currentDate().format(format);
        },

        /**
         * @param {string} format
         *
         * @returns {string}
         */
        currentDatetimeFormatted(format = 'DD/MMM/YY h:mm A') {
            return this.currentDate().format(format);
        },

        /**
         * @param {string} dateString
         *
         * @returns {boolean}
         */
        isInPast(dateString) {
            const timezone = this.authUser.timezone || momentTz.tz.guess();
            const date = this.fromServerDatetimeFormat(dateString).tz(timezone);
            const now = this.currentDate();

            return date.diff(now) < 0;
        },

        /**
         * @param {string} dateString
         *
         * @returns {boolean}
         */
        isInFuture(dateString) {
            const timezone = this.authUser.timezone || momentTz.tz.guess();
            const date = this.fromServerDatetimeFormat(dateString).tz(timezone);
            const now = this.currentDate();

            return date.diff(now) > 0;
        },

        formSubmitShowWait(form) {
            if (typeof form === 'undefined') {
                return;
            }

            // cannot reference modal content directly
            const modal = form.closest('.modal-content');

            if (modal) {
                form = modal;
            }

            const cssClasses = form.className.split(' ');
            const index = cssClasses.indexOf('form-submit-wait');
            const overlay = document.createElement('div');

            overlay.className = 'overlay-submit-wait';
            overlay.innerHTML = '<div><i class="fa fa-spinner fa-5x fa-spin fa-pulse" aria-hidden="true"></i></div>';

            if (index < 0) {
                cssClasses.push('form-submit-wait');
                form.className = cssClasses.join(' ');
            }

            form.appendChild(overlay);
        },

        formSubmitHideWait(form) {
            if (typeof form === 'undefined') {
                return;
            }

            // cannot reference modal content directly
            const modal = form.closest('.modal-content');

            if (modal) {
                form = modal;
            }

            const cssClasses = form.className.split(' ');
            const index = cssClasses.indexOf('form-submit-wait');
            const overlay = form.getElementsByClassName('overlay-submit-wait');

            if (index >= 0) {
                cssClasses.splice(index, 1);
                form.className = cssClasses.join(' ');
            }

            if (overlay.length) {
                form.removeChild(overlay[0]);
            }
        },

        documentShowWait() {
            const overlay = document.createElement('div');

            overlay.className = 'overlay-submit-wait overlay-submit-wait-full';
            overlay.innerHTML = '<div><i class="fa fa-spinner fa-5x fa-spin fa-pulse" aria-hidden="true"></i></div>';

            document.body.classList.toggle('form-submit-wait');
            document.body.appendChild(overlay);
        },

        documentHideWait() {
            const overlay = document.body.getElementsByClassName('overlay-submit-wait');

            document.body.classList.toggle('form-submit-wait');

            if (overlay.length) {
                document.body.removeChild(overlay[0]);
            }
        },

        showOverlayMobileSidebarOpen() {
            const overlay = document.createElement('div');

            overlay.className = 'overlay-mobile-sidebar-open';

            document.body.appendChild(overlay);

            document.body.classList.toggle('disable-scrolling');
        },

        hideOverlayMobileSidebarOpen() {
            const overlay = document.body.getElementsByClassName('overlay-mobile-sidebar-open');

            if (overlay.length) {
                document.body.removeChild(overlay[0]);

                document.body.classList.toggle('disable-scrolling');
            }
        },

        /**
         * Sort contacts by name, first name
         *
         * @param {array} contacts
         * @returns {*}
         */
        sortContactsByName(contacts) {
            return contacts.sort(function (a, b) {
                return `${a.name} ${a.first_name}` > `${b.name} ${b.first_name}`;
            });
        },

        systemIsGlobalManagementSystem() {
            return this.isGlobalManagementSystem;
        },

        systemHasClOnlyFeatures() {
            return this.hasClOnlyFeatures;
        },

        getLatestScrollToComment() {
            return this.latestScrollToComment;
        },

        trans(key, replacements, locale) {
            // TODO this.systemLocale is not always loaded on first use, how to fix?
            return Translation.get(key, replacements, locale || store.state.system_locale);
        },

        humanReadableToSeconds(string, hoursPerDay = null) {
            try {
                string = string.trim();
                string = string.toLowerCase();
                string = string.replace(/\s{2,}/g, ' ');

                // const pattern = '/^\-?([0-9]+(\.[0-9]+)?d\s?)?([0-9]+(\.[0-9]+)?h\s?)?([0-9]+(\.[0-9]+)?m\s?)?$/';
                let isNegative = false;

                hoursPerDay = hoursPerDay ? hoursPerDay : 8;

                if (!string) {
                    return 0;
                }

                if (string.substring(0, 1) === '-') {
                    isNegative = true;
                    string = string.substring(1);
                } else {
                    isNegative = false;
                }

                // Convert string to Array
                const array = string.split(' ');
                const parts = array.map(part => {
                    const number = part.slice(0, -1);
                    const modifier = part[part.length - 1];

                    return {number: number, modifier: modifier};
                });

                const seconds = this.calculateTime(parts, hoursPerDay);

                return isNegative ? -1 * seconds : seconds;
            } catch (e) {
                console.log("Error:", e);
            }
        },

        calculateTime(parts, hoursPerDay) {
            // Get modifier factors
            const factors = {
                s: 1,
                m: 60,
                h: 60 * 60,
                d: hoursPerDay * 60 * 60,
            };

            let seconds = 0;

            parts.map(part => {
                const string = part.number * factors[part.modifier];

                seconds = seconds + parseInt(string.toString().replace(/(\.0*)$/g, ''));
            });

            return seconds;
        },


        secondsToHumanReadable(seconds) {
            const d = Math.floor(seconds / 28800);
            const h = (seconds - (d * 28800)) / 3600;

            return (d ? `${d}d` : '') + (d && h ? ' ' : '') + (h ? `${h}h` : '');
        },
    }

};



// WEBPACK FOOTER //
// ./src/services/AppMixins.js